import {useEffect, useRef, useState} from 'react'
import {reloadForVersion, startVersionMonitor} from './versionMonitor'

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

// Keep a stable owner when a field is removed by an editor's internal tab switch.
function editScope(element: Element): Element | null {
  return (
    element.closest('[role="dialog"], .modal') ||
    element.closest('form, .react-flow, [data-version-edit-scope]')
  )
}

export function AppVersionMonitor() {
  const [availableVersion, setAvailableVersion] = useState<string | null>(null)
  const editedElements = useRef(new Set<Element>())

  useEffect(() => {
    if (!process.env.REACT_APP_BUILD_VERSION) return

    const edits = editedElements.current
    const rememberEdit = (event: Event) => {
      if (!(event.target instanceof Element)) return
      const editable = event.target.closest(EDITABLE_SELECTOR)
      if (editable) edits.add(editScope(editable) || document.body)
    }
    const rememberSubmit = (event: Event) => {
      if (event.target instanceof HTMLFormElement)
        edits.add(editScope(event.target) || event.target)
    }

    const rememberEditorInteraction = (event: Event) => {
      if (!(event.target instanceof Element)) return
      const scope = editScope(event.target)
      // Custom buttons, toggles and canvas gestures do not emit input/change.
      if (scope) edits.add(scope)
    }

    document.addEventListener('pointerdown', rememberEditorInteraction, true)
    document.addEventListener('click', rememberEditorInteraction, true)
    document.addEventListener('keydown', rememberEditorInteraction, true)
    document.addEventListener('drop', rememberEditorInteraction, true)
    document.addEventListener('input', rememberEdit, true)
    document.addEventListener('change', rememberEdit, true)
    document.addEventListener('submit', rememberSubmit, true)

    const stop = startVersionMonitor({
      currentVersion: process.env.REACT_APP_BUILD_VERSION,
      publicUrl: process.env.PUBLIC_URL,
      onUpdate: (version) => {
        setAvailableVersion(version)
        if (!version) return

        // Conservatively retain edits until their form/dialog/editor leaves the page.
        // A submit alone does not prove that an asynchronous save succeeded.
        edits.forEach((element) => {
          if (!element.isConnected) edits.delete(element)
        })
        const focusedEditor = document.activeElement?.closest(EDITABLE_SELECTOR)
        if (edits.size === 0 && !focusedEditor) reloadForVersion(version)
      },
    })

    return () => {
      stop()
      document.removeEventListener('pointerdown', rememberEditorInteraction, true)
      document.removeEventListener('click', rememberEditorInteraction, true)
      document.removeEventListener('keydown', rememberEditorInteraction, true)
      document.removeEventListener('drop', rememberEditorInteraction, true)
      document.removeEventListener('input', rememberEdit, true)
      document.removeEventListener('change', rememberEdit, true)
      document.removeEventListener('submit', rememberSubmit, true)
    }
  }, [])

  if (!availableVersion) return null

  return (
    <div
      role='status'
      className='alert alert-primary shadow d-flex align-items-center flex-wrap gap-3 m-0'
      style={{position: 'fixed', bottom: 20, right: 20, left: 20, zIndex: 11000}}
    >
      <div className='flex-grow-1'>
        <strong>Uma nova versão está disponível.</strong>
        <div>Salve o que estiver preenchendo antes de atualizar.</div>
      </div>
      <button
        type='button'
        className='btn btn-primary btn-sm'
        onClick={() => reloadForVersion(availableVersion, true)}
      >
        Atualizar agora
      </button>
    </div>
  )
}
